'use client'

import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedMessage, useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { ButtonAsA, ButtonSizes } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import buyUrlPrefix from '@/buyer-portal/buyUrlPrefix'
import { useCompareSelection } from '@/components/listings/compare/CompareSelectionProvider'
import { hasUnacceptedTermsRestrictions } from '@/components/listings/ListView/listingHelpers'
import acceptedPoliciesQuery from '@/queries/acceptedPoliciesQuery'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  border: 0.0625rem solid ${Colors.Semantic.Info.highlight};
  border-left-width: 0.25rem;
  border-radius: 0.25rem;
  background-color: ${Colors.Semantic.Info.background};
  ${Typography.Body3}

  @media ${Breakpoints.max.small} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Message = styled.span`
  color: ${Colors.Semantic.Neutral.black};
`

const WarningIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Info.highlight};
  margin-right: 0.5rem;
`

const SellerLink = styled.a`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.BStock.default.lightBackground};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const ApplyButton = styled(ButtonAsA)`
  flex-shrink: 0;

  @media ${Breakpoints.max.small} {
    align-self: stretch;
  }
`

const termsHref = (listing: CombinedListingSearchResult) =>
  `${buyUrlPrefix}/listings/details/${listing.listingId}`

const bySeller = (listings: CombinedListingSearchResult[]) => {
  const sellers = new Map<string, CombinedListingSearchResult>()
  for (const listing of listings) {
    if (!sellers.has(listing.storefrontName)) {
      sellers.set(listing.storefrontName, listing)
    }
  }
  return [...sellers.entries()]
}

const CompareEligibilityBanner = () => {
  const { formatMessage, locale } = useIntl()
  const { selectedListings } = useCompareSelection()
  const decoded = useDecodedAccessToken()
  const { data: acceptedPolicies, status } = acceptedPoliciesQuery.useQuery()

  if (!decoded?.accountId || status === 'pending') return null

  const unapproved = selectedListings.filter((listing) =>
    hasUnacceptedTermsRestrictions(listing, acceptedPolicies ?? null)
  )
  const firstUnapproved = unapproved[0]
  if (!firstUnapproved) return null

  const sellers = bySeller(unapproved)
  const links = new Map(
    sellers.map(([name, listing]) => [
      name,
      <SellerLink
        key={name}
        href={termsHref(listing)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {name}
      </SellerLink>,
    ])
  )

  const sellerLinks = new Intl.ListFormat(locale, { type: 'conjunction' })
    .formatToParts([...links.keys()])
    .map((part) => links.get(part.value) ?? part.value)

  return (
    <BannerContainer data-testid="compare-eligibility-banner">
      <Message>
        <WarningIcon icon={faTriangleExclamation} />
        <FormattedMessage
          id="Listings.ComparePanel.eligibility.message"
          values={{ count: unapproved.length, sellers: sellerLinks }}
        />
      </Message>
      <ApplyButton
        appearance="secondary"
        size={ButtonSizes.small}
        href={termsHref(firstUnapproved)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {formatMessage({ id: 'Listings.ComparePanel.eligibility.apply' })}
      </ApplyButton>
    </BannerContainer>
  )
}

export default CompareEligibilityBanner
